// Helper function to find all instances of a value in the list (case-insensitive, exact match)
export const findAllInstancesByValue = (
  searchValue: string,
  list: any[],
): Array<{ parentKey: string; childKey: string; childValue: string }> => {
  const results: Array<{
    parentKey: string;
    childKey: string;
    childValue: string;
  }> = [];
  const normalizedSearch = searchValue.toLowerCase().trim();

  list.forEach((parent) => {
    if (parent.value?.toLowerCase().trim() === normalizedSearch) {
      results.push({
        parentKey: parent.key,
        childKey: parent.key,
        childValue: parent.value,
      });
    }

    if (parent.children?.length > 0) {
      parent.children.forEach((child: any) => {
        if (child.value?.toLowerCase().trim() === normalizedSearch) {
          results.push({
            parentKey: parent.key,
            childKey: child.key,
            childValue: child.value,
          });
        }
      });
    }
  });

  return results;
};

/** Resolves a single checkbox row by its storage key (e.g. parent\\u001fchild). */
export const findInstancesByChildStorageKey = (
  storageKey: string,
  list: any[],
): Array<{ parentKey: string; childKey: string; childValue: string }> => {
  const results: Array<{
    parentKey: string;
    childKey: string;
    childValue: string;
  }> = [];
  if (!storageKey?.trim() || !list?.length) return results;
  list.forEach((parent) => {
    if (!parent.children?.length) return;
    const child = parent.children.find((c: any) => c.key === storageKey);
    if (child) {
      results.push({
        parentKey: parent.key,
        childKey: child.key,
        childValue: child.value ?? child.key,
      });
    }
  });
  return results;
};

// Helper function to perform local search on hierarchical data structures
export const performLocalSearch = (
  searchTerm: string,
  dataObject: Array<{
    key: string;
    value: string;
    children?: Array<{ key: string; value: string }>;
  }>,
  maxResults = 25,
  page?: number,
): string[] | { data: string[]; hasMore: boolean } => {
  const normalizedSearch = searchTerm.toLowerCase().trim();
  const seen = new Set<string>();

  if (!normalizedSearch || !dataObject || dataObject.length === 0) {
    return page ? { data: [], hasMore: false } : [];
  }

  const sortByPrefix = (arr: string[]): string[] =>
    arr.sort((a, b) => {
      const aPrefix = a.toLowerCase().startsWith(normalizedSearch);
      const bPrefix = b.toLowerCase().startsWith(normalizedSearch);
      if (aPrefix === bPrefix) return 0;
      return aPrefix ? -1 : 1;
    });

  if (page) {
    // Paginated mode: collect all matches, sort prefix-first, then slice by page
    const allResults: string[] = [];

    dataObject.forEach((parent) => {
      if (
        parent.value &&
        parent.value.toLowerCase().includes(normalizedSearch)
      ) {
        if (!seen.has(parent.value)) {
          allResults.push(parent.value);
          seen.add(parent.value);
        }
      }

      if (parent.children && parent.children.length > 0) {
        parent.children.forEach((child) => {
          if (
            child.value &&
            child.value.toLowerCase().includes(normalizedSearch)
          ) {
            if (!seen.has(child.value)) {
              allResults.push(child.value);
              seen.add(child.value);
            }
          }
        });
      }
    });

    sortByPrefix(allResults);

    const PAGE_SIZE = 25;
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return {
      data: allResults.slice(start, end),
      hasMore: allResults.length > end,
    };
  }

  // Legacy mode: collect all matches, sort prefix-first, then cap at maxResults
  const legacyResults: string[] = [];

  dataObject.forEach((parent) => {
    if (parent.value && parent.value.toLowerCase().includes(normalizedSearch)) {
      if (!seen.has(parent.value)) {
        legacyResults.push(parent.value);
        seen.add(parent.value);
      }
    }

    if (parent.children && parent.children.length > 0) {
      parent.children.forEach((child) => {
        if (
          child.value &&
          child.value.toLowerCase().includes(normalizedSearch)
        ) {
          if (!seen.has(child.value)) {
            legacyResults.push(child.value);
            seen.add(child.value);
          }
        }
      });
    }
  });

  sortByPrefix(legacyResults);

  return legacyResults.slice(0, maxResults);
};

export const calculateCheckState = ({
  checkedChild,
  totalChild,
}: {
  checkedChild: number;
  totalChild: number;
}): boolean | 'indeterminate' => {
  if (checkedChild === 0) return false;
  if (checkedChild > 0 && checkedChild < totalChild) return 'indeterminate';
  return true;
};

export const getCheckedKeys = (
  obj: any,
  include = true,
  showExactMatch?: boolean,
): string[] => {
  let result = [];

  Object.values(obj).forEach((item: any) => {
    const shouldInclude =
      include && item?.checked === true && !item?.isExcluded;
    const shouldExclude =
      !include && item?.checked === true && item?.isExcluded;

    if (shouldInclude || shouldExclude) {
      result.push(item?.key);
      if (showExactMatch && item?.children) {
        result = result.concat(
          getCheckedKeys(item.children, true),
          getCheckedKeys(item.children, false),
        );
      }
    } else if (item?.children && item?.checked === 'indeterminate') {
      result = result.concat(getCheckedKeys(item.children, include));
    } else if (item?.checked === false) {
      const checkedChildren = item?.children?.filter(
        (child) => child?.checked === true,
      );
      if (checkedChildren?.length) {
        result = result.concat(getCheckedKeys(checkedChildren, include));
      }
    }
  });

  return result;
};

export type CompanySuggestion = {
  companyId: number;
  companyName: string;
  companyDomain: string;
  companyWebsite: string;
  companyLogoUrl: string;
};

export type SuggestionItem =
  | string
  | {
      value: string;
      type: string;
      emoji: string;
    }
  | CompanySuggestion;

export const isV2Suggestion = (
  suggestion: SuggestionItem,
): suggestion is { value: string; type: string; emoji: string } =>
  typeof suggestion !== 'string' && 'emoji' in suggestion;

export const isCompanySuggestion = (
  suggestion: SuggestionItem,
): suggestion is CompanySuggestion =>
  typeof suggestion !== 'string' && 'companyId' in suggestion;

// Converts ISO 3166-1 alpha-2 country code to a Unicode flag emoji.
// Example: "US" → "🇺🇸". Returns null for invalid country code.
export const getFlagCountryCode = (countryCode: string): string | null => {
  const code = String(countryCode || '')
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;

  const A = 65;
  const REGIONAL_INDICATOR_A = 0x1f1e6;
  const chars = code.split('');
  return String.fromCodePoint(
    REGIONAL_INDICATOR_A + (chars[0].charCodeAt(0) - A),
    REGIONAL_INDICATOR_A + (chars[1].charCodeAt(0) - A),
  );
};

// Converts a Unicode flag emoji (two regional indicator symbols) to an ISO 3166-1 alpha-2
// country code string (e.g. 🇺🇸 → "US"). Returns null for non-flag emoji.
export const getCountryCodeFromFlagEmoji = (emoji: string): string | null => {
  const chars = Array.from(emoji);
  if (chars.length !== 2) return null;
  const codes = chars.map((c) => c.codePointAt(0)!);
  if (!codes.every((c) => c >= 0x1f1e6 && c <= 0x1f1ff)) return null;
  return String.fromCharCode(...codes.map((c) => c - 0x1f1e6 + 65));
};

/** ISO 3166-1 alpha-2 (lowercase) for flag CDN URLs — handles both "us" and 🇺🇸. */
export const resolveFlagImageIso2 = (emoji: string): string | null => {
  const raw = String(emoji || '').trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toLowerCase();
  const fromPair = getCountryCodeFromFlagEmoji(raw);
  return fromPair ? fromPair.toLowerCase() : null;
};

export const getSuggestionValue = (suggestion: SuggestionItem): string => {
  if (typeof suggestion === 'string') return suggestion;
  if (isCompanySuggestion(suggestion)) return suggestion.companyName;
  return suggestion.value;
};

export const getSuggestionKey = (
  suggestion: SuggestionItem,
  index: number,
): string => {
  if (typeof suggestion === 'string') return suggestion;
  if (isCompanySuggestion(suggestion)) return String(suggestion.companyId);
  return `${suggestion.value}-${suggestion.type}-${index}`;
};

export const COMPANY_NAME_MAX_FILTER_COUNT = 10000;

const COMPANY_NAME_FILTER_PARAM_KEYS = [
  'company_ids',
  'excludeCompany_ids',
  'company_name',
  'excludeCompany_name',
] as const;

export const getCompanyNameFilterTotalCount = (
  params: URLSearchParams,
): number =>
  COMPANY_NAME_FILTER_PARAM_KEYS.reduce(
    (sum, key) => sum + params.getAll(key).length,
    0,
  );

export const isCompanyNameFilterLimitReached = (
  filterKey: string,
  params: URLSearchParams,
): boolean =>
  filterKey === 'company_ids' &&
  getCompanyNameFilterTotalCount(params) >= COMPANY_NAME_MAX_FILTER_COUNT;

export const getCompanyNameFilterLimitTooltip = (): string =>
  `You can add up to ${COMPANY_NAME_MAX_FILTER_COUNT.toLocaleString()} companies. Remove one to add another.`;

export const getCompanyNameFilterLimitInputError = (): string =>
  'You can add up to 10,000 companies at a time for more relevant results. Remove one to add another.';

/** Values that parse as numbers are stored in `company_ids`; text goes to `company_name`. */
export const isNumericCompanyFilterValue = (value: string): boolean => {
  const trimmed = String(value ?? '').trim();
  return trimmed !== '' && !Number.isNaN(Number(trimmed));
};
